import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatAccordion } from '@angular/material/expansion';
import { AuthService } from '@klickdata/core/auth';
import { AppValidators, FormHelper } from '@klickdata/core/form';
import { MediaType } from '@klickdata/core/media/src/media-type';
import { MediaService } from '@klickdata/core/media/src/media.service';
import { MobileService, SideNaveActionsTypes, SideNaveDataTypes } from '@klickdata/core/mobile';
import { ResourceMediaTypes } from '@klickdata/core/question/src/question-type/question-type';
import { AppScope, Resource, ResourceTypes } from '@klickdata/core/resource';
import { ResourceItem, ResourceItemTypes } from '@klickdata/core/resource-item';
import { User } from '@klickdata/core/user';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { catchError, filter, first, map, shareReplay, switchMap, takeUntil } from 'rxjs/operators';
import { ResourceBuilderService } from '../resource-builder.service';
import { QuestionService } from '../shared/question.service';
import { ResourceItemFormComponent } from './resource-item-form.component';
import { MessageErrorComponent } from '@klickdata/core/message/src/message-error/message-error.component';
import { MessageService } from '@klickdata/core/message';
import { Router } from '@angular/router';
import { MatMenuTrigger } from '@angular/material/menu';
import { Customer } from '@klickdata/core/customer';
import { ResourceItemMaterialFormComponent } from './resource-item-material-form/resource-item-material-form.component';
import * as moment from 'moment';
import { ResourceService } from '@klickdata/core/resource/src/resource.service';

@Component({
    selector: 'app-resource-items',
    templateUrl: './resource-items.component.html',
    styleUrls: ['./resource-items.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResourceItemsComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
    @ViewChild('otherToolsMenuTrigger') otherToolsMenuTrigger: MatMenuTrigger;
    @Input()
    public itemFormArray: FormArray;
    @Input()
    public resourceForm: FormGroup;

    @Input()
    public items: ResourceItem[];

    @Input()
    public usePoints = true;
    @Output() onSubmitThenRoute: EventEmitter<Resource> = new EventEmitter<Resource>();

    public reorder = false;
    /**
     * Index of expansion panel to expand.
     */
    public expanded: number;
    public allExpanded: boolean;
    private destroy: Subject<boolean> = new Subject<boolean>();
    private timeOutIDs: number[] = [];
    public published: boolean;
    private loadedMenuItems: Set<number> = new Set<number>();
    private itemInfoCache: Map<number, Observable<string>> = new Map<number, Observable<string>>();
    @ViewChild(MatAccordion, { static: true }) accordion: MatAccordion;
    private user: User;
    @ViewChild('res_item_ref') resItemFormComp: ResourceItemFormComponent;
    @ViewChild('res_item_ref_material') resItemFormCompMaterial: ResourceItemMaterialFormComponent;
    public loadingUploader: Subject<boolean> = new Subject<boolean>();
    public qsImgLoader: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    public resource: Resource;
    public sortingPrio: FormControl = new FormControl(null);
    public customer: Customer;
    AppScope = AppScope;
    constructor(
        protected resourceBuilderService: ResourceBuilderService,
        protected resourceService: ResourceService,
        protected fb: FormBuilder,
        protected changeRef: ChangeDetectorRef,
        protected elementRef: ElementRef,
        protected questionService: QuestionService,
        protected auth: AuthService,
        protected router: Router,
        protected mobileService: MobileService,
        protected mediaService: MediaService,
        protected messageService: MessageService
    ) {
        this.auth
            .getUser()
            .pipe(takeUntil(this.destroy), first())
            .subscribe((user) => {
                this.user = user;
                this.changeRef.markForCheck();
            });
        this.auth
            .getCustomer()
            .pipe(takeUntil(this.destroy))
            .subscribe((customer) => (this.customer = customer));
    }
    public ngOnInit(): void {
        this.itemFormArray.valueChanges
            .pipe(takeUntil(this.destroy))
            .subscribe(() => this.resourceBuilderService.updateForm(<FormGroup>this.itemFormArray.parent));

        this.resourceBuilderService
            .onCreateItem()
            .pipe(takeUntil(this.destroy))
            .subscribe((data) => this.createItem(data));

        this.resourceBuilderService
            .onSort()
            .pipe(takeUntil(this.destroy))
            .subscribe((value: boolean) => {
                this.reorder = value;
                this.expanded = -1; // colapse expanded item when start reordering.
                this.changeRef.markForCheck();
            });

        this.resourceBuilderService
            .isLoading()
            .pipe(takeUntil(this.destroy))
            .subscribe((isLoading) => {
                if (!isLoading) {
                    // Refresh item panels after saving completed, that clear removed items from displyed panels
                    this.changeRef.markForCheck();
                }
            });

        this.resourceBuilderService
            .getResource()
            .pipe(takeUntil(this.destroy))
            .subscribe((resource) => (this.resource = resource));
    }
    public ngAfterViewInit() {
        this.resourceBuilderService
            .getItemsFoldingState()
            .pipe(takeUntil(this.destroy))
            .subscribe((isAllFolded) => {
                this.allExpanded = !isAllFolded;
                isAllFolded ? this.accordion.closeAll() : this.accordion.openAll();
                this.changeRef.markForCheck();
            });
    }
    public ngOnChanges(): void {
        if (this.items && this.items.length) {
            this.updateItems(this.items);
            this.published = this.resourceBuilderService.published;
        }
    }

    public ngOnDestroy(): void {
        this.destroy.next(true);
        this.destroy.unsubscribe();
        this.timeOutIDs.forEach((id) => clearTimeout(id));
        this.itemInfoCache.clear();
    }

    public onOpen(index: number) {
        this.expanded = index;
    }
    public drop(previousIndex: number, currentIndex: number) {
        moveItemInArray(this.itemFormArray.controls, previousIndex, currentIndex);
        moveItemInArray(this.items, previousIndex, currentIndex);
        this.resourceBuilderService.submit();
    }
    getItemInfo(item: ResourceItem): Observable<string> {
        if (!item?.child_resource_id) {
            return of('');
        }

        if (!this.itemInfoCache.has(item.child_resource_id)) {
            const infoObservable = this.resourceService.getResource(item.child_resource_id, 'author').pipe(
                takeUntil(this.destroy),
                shareReplay(1),
                map((resource) => {
                    return [
                        $localize`Link: ${resource.id}`,
                        $localize`Author: ${resource.author?.name ?? ''}`,
                        $localize`Last Edit: ${item.updated_at ? moment(item.updated_at).format('YYMMDD_HHmm') : ''}`,
                    ].join('\n');
                })
            );
            this.itemInfoCache.set(item.child_resource_id, infoObservable);
        }

        return this.itemInfoCache.get(item.child_resource_id)!;
    }
    public toggleDisable(ev: Event, item: AbstractControl) {
        ev.stopPropagation();

        if (!item.disabled) {
            item.disable();
        } else {
            item.enable();
        }
        item.markAsDirty();
    }

    public editable(item: ResourceItem) {
        return item && item.editable(this.user, this.resource);
    }
    public onEdit(item: ResourceItem) {
        if (item.item_type_value === ResourceItemTypes.MATERIAL) {
            this.resItemFormCompMaterial?.onEdit(-1);
        } else {
            this.resItemFormComp.itemDirective?.onEdit();
        }
    }
    change() {
        this.resItemFormComp.itemDirective?.onChangeMode(true);
    }
    updateResource(item: AbstractControl) {
        const resourceItem = this.items.find((i) => i.id === item.value.id);
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.UPDATE_RESOURCE_ITEM,
            data: {
                title: $localize`Update ${item.value.title ?? item.value.name}`,
                resId: item.value.child_resource_id,
                resourceItem: resourceItem,
                type: 'updateResourceItemFromResource',
            },
        });
        this.mobileService
            .getSideNavAction()
            .pipe(
                filter((action) => action === SideNaveActionsTypes.POSITIVE),
                switchMap(() => this.mobileService.getSideNavResponseData()),
                filter((data) => data.type === 'updateResourceItemFromResource'),
                map((data) => data.resource)
            )
            .subscribe((resource) => {
                item.patchValue({
                    name: resource.title,
                    title: resource.title,
                    description: resource.description,
                    bullets: resource.bullets,
                    instructions: resource.instructions,
                });
                this.changeRef.markForCheck();
            });
    }
    public duplicate(ev: Event, item: AbstractControl) {
        ev.stopPropagation();
        // TODO handle all types
        if (this.resourceBuilderService.resource_type_id === ResourceTypes.GeneralCoursePlan) {
            this.createItem({
                item_type_value: item.get('item_type_value').value,
                name: item.get('name').value,
                title: item.get('title').value,
                mandatory: item.get('mandatory').value,
                percentage: item.get('percentage').value,
                repeats: item.get('repeats').value,
                show_correct_alts: item.get('show_correct_alts').value,
                description: item.get('description').value,
                bullets: item.get('bullets').value,
                instructions: item.get('instructions').value,
                child_resource_id: item.get('child_resource_id').value,
                child_resource_language_id: item.get('child_resource_language_id').value,
                child_resource_customer_id: item.get('child_resource_customer_id').value,
                child_resource_author_id: item.get('child_resource_author_id').value,
                child_resource_items_count: item.get('child_resource_items_count').value,
                metadata: item.get('metadata').value,
            });
        } else {
            const question = item.get('question');
            this.questionService
                .duplicateFormGroup(question, this.usePoints, item.get('id').value)
                .subscribe((duplicatedQuestion) => {
                    const duplicatedItem = this.createItem({
                        item_type_value: item.get('item_type_value').value,
                        questionForm: duplicatedQuestion,
                        name: item.get('name').value,
                        description: item.get('description').value,
                        learning_field: item.get('learning_field')?.value,
                        references: item.get('references')?.value,
                    });
                });
        }
    }

    public createItem(data: {
        item_type_value?: ResourceItemTypes;
        questionForm?: FormGroup;
        title?: string;
        name?: string;
        mandatory?: boolean;
        percentage?: number;
        repeats?: number;
        show_correct_alts?: string;
        description?: string;
        instructions?: string;
        learning_field?: string;
        references?: string;
        child_resource_id?: number;
        child_resource_language_id?: number;
        collapse?: boolean;
        child_resource_customer_id?: number;
        child_resource_author_id?: number;
        child_resource_items_count?: number;
        isEmpty?: boolean;
        bullets?: string;
        metadata?: any;
    }): FormGroup {
        this.items = [...this.items, new ResourceItem(data)];
        const isResource = this.isItemTypeResource(data.item_type_value);

        const item = this.fb.group({
            id: [null],
            title: [data.title],
            name: [data.name, AppValidators.notDelete(Validators.required)],
            description: [data.description],
            bullets: [data.bullets],
            instructions: [data.instructions],
            mandatory: [
                null === data.mandatory ? data.mandatory : true,
                isResource ? AppValidators.notDelete(Validators.required) : null,
            ],
            repeats: [data.repeats ? data.repeats : null],
            show_correct_alts: [data.show_correct_alts ? data.show_correct_alts : 'always'],
            percentage: [data.percentage],
            learning_field: [data.learning_field],
            references: [data.references],
            item_type_value: [data.item_type_value, AppValidators.notDelete(Validators.required)],
            question: data.questionForm
                ? data.questionForm
                : this.fb.group({}, [
                      ResourceItemTypes.QUESTION === data.item_type_value
                          ? AppValidators.notDelete(Validators.required)
                          : null,
                  ]),
            child_resource_language_id: [
                data.child_resource_language_id,
                isResource ? AppValidators.notDelete(Validators.required) : null,
            ],

            child_resource_id: [data.child_resource_id],
            child_resource_customer_id: [data.child_resource_customer_id],
            child_resource_author_id: [data.child_resource_author_id],
            child_resource_items_count: [data.child_resource_items_count],
            metadata: this.fb.group({
                visibility: [data.metadata?.visibility ?? true],
            }),
        });
        // handle user can edit resource flag.
        FormHelper.markForm(item);
        if (
            isResource &&
            !this.user?.canEdit({
                customer_id: data.child_resource_customer_id,
                author_id: data.child_resource_author_id,
            })
        ) {
            item.get('child_resource_language_id').disable();
        }
        this.itemFormArray.push(item);

        this.onOpen(
            data.collapse || this.resourceBuilderService.selectedWOKQuestionType.getValue()
                ? -1
                : this.itemFormArray.length - 1
        );
        this.changeRef.markForCheck();
        this.timeOutIDs.push(
            window.setTimeout(() => {
                const elements = this.elementRef.nativeElement.getElementsByTagName('mat-expansion-panel');
                if (elements.length && !this.resourceBuilderService.selectedWOKQuestionType.getValue()) {
                    elements[elements.length - 1].scrollIntoView({
                        behavior: 'smooth',
                        block: 'end',
                        inline: 'nearest',
                    });
                }
                if (data.isEmpty) {
                    FormHelper.resetForm(item, true);
                }
            }, 300)
        );
        return item;
    }

    protected updateItems(items: ResourceItem[]) {
        this.itemFormArray.controls.splice(0, this.itemFormArray.controls.length);
        items
            .map((item) => {
                const isResource = this.isItemTypeResource(item.item_type_value);
                const group = this.fb.group({
                    id: [item.id],
                    title: [item.title],
                    name: [item.name],
                    description: [item.description],
                    bullets: [item.bullets],
                    instructions: [item.instructions],
                    mandatory: [item.mandatory, isResource ? AppValidators.notDelete(Validators.required) : null],
                    percentage: [item.passing_level?.percentage],
                    repeats: [item.passing_level?.repeats],
                    show_correct_alts: [item.show_correct_alts],
                    item_type_value: [item.item_type_value, AppValidators.notDelete(Validators.required)],
                    question: this.fb.group({}),
                    child_resource_language_id: [
                        item.child_resource_language_id,
                        isResource ? AppValidators.notDelete(Validators.required) : null,
                    ],
                    child_resource_id: [item.child_resource_id],
                    child_resource_customer_id: [item.child_resource_customer_id],
                    child_resource_author_id: [item.child_resource_author_id],
                    child_resource_items_count: [item.child_resource_items_count],
                    metadata: this.fb.group({
                        visibility: [item.metadata?.visibility ?? true],
                    }),
                });

                // handle user can edit resource flag.
                if (
                    isResource &&
                    !this.user?.canEdit({
                        customer_id: item.child_resource_customer_id,
                        author_id: item.child_resource_author_id,
                    })
                ) {
                    group.get('child_resource_language_id').disable();
                }

                return group;
            })
            .forEach((group) => this.itemFormArray.push(group, { emitEvent: false }));
    }

    private isItemTypeResource(type: string | ResourceItemTypes) {
        return (
            type === ResourceItemTypes.COURSE ||
            type === ResourceItemTypes.TEST ||
            type === ResourceItemTypes.MATERIAL ||
            type === ResourceItemTypes.SURVEY ||
            type === ResourceItemTypes.EVENT ||
            type === ResourceItemTypes.SCORM
        );
    }

    public isTest(typeValue) {
        return typeValue && typeValue.indexOf('test') !== -1;
    }
    public displayCorrectIcon(item) {
        if (item.indexOf('test') !== -1) {
            return 'test';
        } else if (item.indexOf('survey') !== -1) {
            return 'survey';
        } else if (item.indexOf('e-course') !== -1) {
            return 'e-course';
        } else if (item.indexOf('text') !== -1) {
            return 'text';
        } else if (item.indexOf('url') !== -1) {
            return 'url';
        } else if (item.indexOf('doc') !== -1) {
            return 'doc';
        } else if (item.indexOf('question') !== -1) {
            return 'question';
        } else if (item.indexOf('material') !== -1) {
            return 'material';
        } else if (item.indexOf('course') !== -1) {
            return 'course';
        } else if (item.indexOf('event') !== -1) {
            return 'event';
        } else if (item.indexOf('scorm') !== -1) {
            return 'scorm';
        }
    }

    public getTooltipText(checked: boolean) {
        return checked
            ? $localize`:@@ChangeToComplimentaryPartOfTheCourse:Change to complimentary part of the Course`
            : $localize`:@@ChangeToMandatoryPartOfTheCourse:Change to mandatory part of the Course`;
    }

    public getRepeatsLabel(item: ResourceItem) {
        if (item.repeats == 0) {
            return $localize`No retries allowed`;
        } else if (item.repeats > 0) {
            return $localize`Retry limits: ${item.repeats}`;
        } else {
            return $localize`Infinte retries`;
        }
    }

    public getPassingLevelLabel(item: ResourceItem) {
        if (item.percentage > 0) {
            return $localize`Passing percentage: ${item.percentage} %`;
        } else {
            return $localize`No passing percentage`;
        }
    }

    public setPassingLevel(checked: boolean, item: AbstractControl) {
        if (checked) {
            item.get('percentage').patchValue(75);
        } else {
            item.get('percentage').patchValue(null);
        }
        FormHelper.markForm(item);
    }

    public getCorrectAnswersTooltip(show_correct_alts: string) {
        switch (show_correct_alts) {
            case 'always':
                return $localize`Always view correct answers in test results`;
            case 'never':
                return $localize`Hide correct answers in test results`;
            case 'passed':
                return $localize`View correct answers in test results only if the test is passed`;
            default:
                return $localize`View correct answers in test results`;
        }
    }
    public addMedia(item: AbstractControl, resItem: ResourceItem) {
        this.changeRef.markForCheck();
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.ADD_RESOURCE_MEDIA,
            data: {
                icon: 'add_photo_alternate',
                title: $localize`Add an image`,
                description: $localize`Please select your preferred way to add an image.`,
                type: ResourceItemTypes.QUESTION,
            },
        });
        this.mobileService
            .getSideNavAction()
            .pipe(
                filter((action) => action === SideNaveActionsTypes.POSITIVE),
                switchMap(() => this.mobileService.getSideNavResponseData()),
                filter((data) => data.type === ResourceItemTypes.QUESTION),
                takeUntil(this.destroy)
            )
            .subscribe((data) => {
                if (data && data.value) {
                    this.initUpload(data, item as FormGroup, resItem);
                    item.markAsDirty();
                }
            });
    }

    private initUpload(data: any, item: FormGroup, resItem: ResourceItem) {
        this.qsImgLoader.next({ id: resItem.id, state: true });
        if (data.value === ResourceMediaTypes.FILE) {
            const question = <FormGroup>item.get('question');
            question.patchValue({ media_id: data.mediaId });
            question.markAsDirty();
            this.changeRef.markForCheck();
        } else {
            this.resItemFormComp?.mediaCompRef.saving.emit(true);
            this.mediaService
                .uploadMediaByLink({
                    prompt: data.aiText,
                    type: MediaType.PROMPT,
                    scope_id: AppScope.QUESTIONS,
                })
                .pipe(
                    takeUntil(this.destroy),
                    catchError((err) => {
                        this.handleAIRequestFailure(err);
                        return of(null);
                    })
                )
                .subscribe((media) => {
                    if (media && media.id) {
                        this.resItemFormComp?.mediaCompRef.saving.emit(false);
                        this.qsImgLoader.next(null);
                        const question = <FormGroup>item.get('question');
                        question.patchValue({ media_id: media.id });
                        question.markAsDirty();
                        resItem.media.type = 'gpt';
                        this.changeRef.markForCheck();
                    }
                });
        }
    }
    handleAIRequestFailure(err) {
        if (err && err.error && err.error.error) {
            this.messageService.openMessage(MessageErrorComponent, err.error.error.messages.join('/n'));
        }
    }
    getChildResourceNameTranslation(id: number) {
        return this.items.find((i) => i.id == id)?.child_resource_name_translation;
    }
    getResItemNameTranslation(id: number) {
        return (
            this.items.find((i) => i.id == id)?.child_resource_name_translation ??
            this.customer.scope_names[AppScope.MATERIAL] ??
            AppScope.label(AppScope.MATERIAL)
        );
    }
    getScormItemFileOrigin(id: number): string | undefined {
        const item = this.items.find((i) => i.id == id);
        if (item && item.id && item.item_type_value === ResourceItemTypes.SCORM) {
            return item.child_resource_origin_file;
        }
        return undefined;
    }

    public isMenuLoaded(index: number): boolean {
        return this.loadedMenuItems.has(index);
    }

    public onMenuOpened(index: number): void {
        this.loadedMenuItems.add(index);
        this.changeRef.markForCheck();
    }
}
